import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PrivacyPolicyModal from '../PrivacyPolicyModal';
import TermsConditionsModal from '../TermsConditionsModal';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [siteSettings, setSiteSettings] = useState({
    schoolName: 'East End Prep',
    address: '123 Eagle Way, East End, NY 12345',
    phone: '(555) 123-PREP',
    email: 'athletics@eastendprep.edu',
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    youtubeUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/site-settings`);
        if (response.ok) {
          const data = await response.json();
          setSiteSettings(data);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSiteSettings();
  }, []);

  if (loading) {
    return (
      <footer className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">Loading...</div>
        </div>
      </footer>
    );
  }

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Sports', href: '/sports' },
    { name: 'News', href: '/news' },
    { name: 'Events', href: '/events' },
    { name: 'Contact', href: '/contact' }
  ];

  const sportsLinks = [
    { name: 'Football', href: '/sports/football' },
    { name: 'Basketball', href: '/sports/basketball' },
    { name: 'Soccer', href: '/sports/soccer' },
    { name: 'Track & Field', href: '/sports/track-field' },
    { name: 'All Sports', href: '/sports' }
  ];

  return (
    <footer className="bg-primary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* School Information */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                {/* Eagle Logo */}
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                  <svg 
                    className="w-7 h-7 text-primary" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{siteSettings.schoolName}</h3>
                  <p className="text-accent font-medium">Eagles Believe, Eagles Achieve</p>
                </div>
              </div>
              
              <p className="text-blue-100 mb-6 max-w-md">
                Empowering student-athletes to excel both on the field and in the classroom. 
                Join the Eagle family and soar to new heights.
              </p>
              
              {/* Contact Information */}
              <div className="space-y-2 text-blue-100">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <span>{siteSettings.address}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span>{siteSettings.phone}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span>{siteSettings.email}</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-blue-100 hover:text-accent transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sports */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Sports</h4>
              <ul className="space-y-2">
                {sportsLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-blue-100 hover:text-accent transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Social Media and Bottom Bar */}
        <div className="border-t border-blue-400 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Social Media Links */}
            <div className="flex items-center space-x-4">
              <span className="text-blue-100 text-sm">Follow Us:</span>
              
              {/* Facebook */}
              {siteSettings.facebookUrl && (
                <a
                  href={siteSettings.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-100 hover:text-accent transition-colors duration-200"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
              
              {/* Twitter */}
              {siteSettings.twitterUrl && (
                <a
                  href={siteSettings.twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-100 hover:text-accent transition-colors duration-200"
                  aria-label="Twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
              )}
              
              {/* Instagram */}
              {siteSettings.instagramUrl && (
                <a
                  href={siteSettings.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-100 hover:text-accent transition-colors duration-200"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.875 2.026-1.297 3.323-1.297s2.448.422 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.404h-1.507V6.077h1.507v1.507zm-.85 5.95c0-.85-.422-1.507-.928-2.026-.507-.507-1.177-.928-2.026-.928s-1.507.422-2.026.928c-.507.507-.928 1.177-.928 2.026s.422 1.507.928 2.026c.507.507 1.177.928 2.026.928s1.507-.422 2.026-.928c.507-.507.928-1.177.928-2.026z"/>
                  </svg>
                </a>
              )}
              
              {/* YouTube */}
              {siteSettings.youtubeUrl && (
                <a
                  href={siteSettings.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-100 hover:text-accent transition-colors duration-200"
                  aria-label="YouTube"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              )}
            </div>

            {/* Copyright */}
            <div className="text-blue-100 text-sm text-center md:text-right">
              <p>&copy; {currentYear} {siteSettings.schoolName} Middle School Athletics. All rights reserved.</p>
              <p className="mt-1">
                <button 
                  onClick={() => setShowPrivacyModal(true)}
                  className="hover:text-accent transition-colors duration-200 underline"
                >
                  Privacy Policy
                </button>
                {' | '}
                <button 
                  onClick={() => setShowTermsModal(true)}
                  className="hover:text-accent transition-colors duration-200 underline"
                >
                  Terms and Conditions
                </button>
              </p>
              <p className="mt-1">
                <a 
                  href="https://www.blueskyaiautomation.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors duration-200"
                >
                  Website Created by <span className="underline">BlueSkyAiAutomation</span>
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
      
      {/* Terms and Conditions Modal */}
      <TermsConditionsModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />
    </footer>
  );
};

export default Footer;